var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AmbientLight, Box3, DirectionalLight, HalfFloatType, LinearToneMapping, Object3D, PCFSoftShadowMap, PerspectiveCamera, PMREMGenerator, Scene, SRGBColorSpace, Vector3, WebGLRenderer, } from 'three';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { BinarySearchCameraOptimizer } from './BinarySearchCameraOptimizer';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { getMeasurableObjects } from '../scene-utils';
export const emptyPadding = {
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
};
export class GlbConverter {
    constructor(width, height, config) {
        this.width = width;
        this.height = height;
        this.config = config;
        this.padding2D = emptyPadding;
    }
    convertGltfToBase64(url, hdr, onProgress) {
        return __awaiter(this, void 0, void 0, function* () {
            const gltfModel = yield this.loadModel(url, onProgress);
            return this.convertObject3DToBase64(gltfModel.scene, hdr);
        });
    }
    convertObject3DToBase64(node_1, hdr_1) {
        return __awaiter(this, arguments, void 0, function* (node, hdr, options = {}) {
            var _a;
            const container = new Object3D();
            container.add(node);
            const canvas = document.createElement('canvas');
            canvas.width = this.width;
            canvas.height = this.height;
            const renderer = new WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
            renderer.outputColorSpace = SRGBColorSpace;
            renderer.setPixelRatio(window.devicePixelRatio);
            renderer.shadowMap.enabled = true;
            renderer.shadowMap.type = PCFSoftShadowMap;
            renderer.toneMapping = LinearToneMapping;
            renderer.toneMappingExposure = 0.65;
            renderer.setSize(this.width, this.height);
            renderer.setClearColor(0xffffff, 1);
            const scene = new Scene();
            const pMREMGenerator = new PMREMGenerator(renderer);
            pMREMGenerator.compileEquirectangularShader();
            const envTexture = yield this.loadEnvTexture(hdr);
            scene.environment = pMREMGenerator.fromEquirectangular(envTexture).texture;
            const ambientLight = new AmbientLight(0xffffff, 0.6);
            scene.add(ambientLight);
            const directionalLight = new DirectionalLight(0xffffff, 0.8);
            directionalLight.position.set(10, 10, 10);
            scene.add(directionalLight);
            scene.add(container);
            const camera = new PerspectiveCamera(30, this.width / this.height, 0.01, 180);
            const cameraPosition = (_a = options.cameraPosition) !== null && _a !== void 0 ? _a : 'perspectiveLeft';
            this.fitCameraToObject3D(camera, container, cameraPosition);
            renderer.render(scene, camera);
            const dataURL = canvas.toDataURL('image/png');
            return dataURL;
        });
    }
    convertBufferToBase64(data, hdr) {
        return new Promise((resolve, reject) => {
            const loader = this.createGltfLoader();
            loader.parse(data, '', (gltf) => {
                const scene = gltf.scene;
                const base64Image = this.convertObject3DToBase64(scene, hdr);
                resolve(base64Image);
            }, (error) => {
                console.error('Parsing error:', error);
                reject(null);
            });
        });
    }
    fitCameraToObject3D(camera, node, cameraPosition) {
        const { position, lookAt, far } = this.getCameraParams(camera, node, cameraPosition);
        camera.far = far;
        camera.position.copy(position);
        camera.lookAt(lookAt);
        camera.updateProjectionMatrix();
        return lookAt;
    }
    getCameraParams(camera, node, cameraPosition) {
        const nodeClone = getMeasurableObjects(node);
        const box = new Box3().setFromObject(nodeClone);
        const size = box.getSize(new Vector3());
        const center = box.getCenter(new Vector3());
        const position = center.clone().add(this.getCameraPosition(cameraPosition));
        camera.position.set(position.x, position.y, position.z);
        camera.lookAt(center);
        const maxSize = Math.max(size.x, size.y, size.z, 100);
        const minSize = 0.01;
        const cameraOptimizer = BinarySearchCameraOptimizer.create({ width: this.width, height: this.height }, minSize, maxSize * maxSize);
        const desiredState = [...camera.position.toArray(), ...center.toArray()];
        const infinity = 1e100;
        const width = this.width;
        const height = this.height;
        const { state: targetState, distance } = cameraOptimizer.optimizeCameraPosition(camera, desiredState, nodeClone, [
            { x: -infinity, width: infinity * 2 + width, y: -infinity, height: infinity },
            { x: -infinity, width: infinity * 2 + width, y: height - this.padding2D.bottom, height: height + infinity },
            { x: -infinity, width: infinity, y: 0, height: height },
            { x: width, width: width + infinity, y: 0, height: height },
        ]);
        return {
            position: new Vector3(targetState[0], targetState[1], targetState[2]),
            lookAt: new Vector3(targetState[3], targetState[4], targetState[5]),
            far: Math.max(distance * distance, 100),
        };
    }
    loadModel(url, onProgress) {
        const loader = this.createGltfLoader();
        return new Promise((resolve, reject) => {
            loader.load(url, (gltf) => {
                resolve(gltf);
            }, onProgress, (error) => {
                reject(error);
            });
        });
    }
    loadEnvTexture(url, onProgress) {
        const loader = new RGBELoader();
        return new Promise((resolve, reject) => {
            loader.setDataType(HalfFloatType).load(url, (texture) => {
                resolve(texture);
            }, onProgress, (error) => {
                reject(error);
            });
        });
    }
    getCameraPosition(cameraPosition) {
        switch (cameraPosition) {
            case 'perspectiveRight':
                return sphericalToVector3({ type: 'spherical', polarDegrees: 72.5, azimuthDegrees: 36.5 });
            case 'perspectiveLeft': {
                return sphericalToVector3({ type: 'spherical', polarDegrees: 72.5, azimuthDegrees: -36.5 });
            }
            default:
                return sphericalToVector3(cameraPosition);
        }
    }
    createGltfLoader() {
        var _a, _b, _c;
        const loader = new GLTFLoader();
        if ((_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.dracoLoader) === null || _b === void 0 ? void 0 : _b.enabled) {
            const dracoLoader = new DRACOLoader();
            if ((_c = this.config) === null || _c === void 0 ? void 0 : _c.dracoLoader.decoderPath) {
                dracoLoader.setDecoderPath(this.config.dracoLoader.decoderPath);
            }
            loader.setDRACOLoader(dracoLoader);
        }
        return loader;
    }
}
function sphericalToVector3(position) {
    const { azimuthDegrees, polarDegrees } = position;
    const globalAzimuth = Math.PI / 2 - (azimuthDegrees * Math.PI) / 180;
    const globalPolar = (polarDegrees * Math.PI) / 180;
    const x = Math.sin(globalPolar) * Math.cos(globalAzimuth);
    const z = Math.sin(globalPolar) * Math.sin(globalAzimuth);
    const y = Math.cos(globalPolar);
    return new Vector3(x, y, z);
}
//# sourceMappingURL=GlbConverter.js.map